export interface ViewportPoint {
  readonly x: number;
  readonly y: number;
}

export interface ViewportState {
  readonly offsetX: number;
  readonly offsetY: number;
  readonly scale: number;
}

export interface ViewportScaleBounds {
  readonly maxScale: number;
  readonly minScale: number;
}

export interface ViewportConfig {
  readonly bounds?: ViewportScaleBounds;
  readonly defaultState?: Partial<ViewportState>;
}

export interface ViewportModel {
  readonly bounds: ViewportScaleBounds;
  readonly defaultState: ViewportState;
  clampScale: (scale: number) => number;
  createState: (state?: Partial<ViewportState>) => ViewportState;
  panBy: (state: ViewportState, delta: ViewportPoint) => ViewportState;
  panTo: (state: ViewportState, offset: ViewportPoint) => ViewportState;
  reset: () => ViewportState;
  setScale: (state: ViewportState, scale: number) => ViewportState;
  stageToWorld: (state: ViewportState, point: ViewportPoint) => ViewportPoint;
  worldToStage: (state: ViewportState, point: ViewportPoint) => ViewportPoint;
  zoomAtWorld: (
    state: ViewportState,
    scale: number,
    worldAnchor: ViewportPoint,
    stageAnchor?: ViewportPoint,
  ) => ViewportState;
  zoomTo: (state: ViewportState, scale: number, stageAnchor?: ViewportPoint) => ViewportState;
  zoomBy: (state: ViewportState, factor: number, stageAnchor?: ViewportPoint) => ViewportState;
}

export const DEFAULT_VIEWPORT_BOUNDS = Object.freeze<ViewportScaleBounds>({
  maxScale: 3,
  minScale: 0.5,
});

export const DEFAULT_VIEWPORT_STATE = Object.freeze<ViewportState>({
  offsetX: 0,
  offsetY: 0,
  scale: 1,
});

const ORIGIN: ViewportPoint = Object.freeze({ x: 0, y: 0 });

const assertFinite = (value: number, name: string) => {
  if (!Number.isFinite(value)) {
    throw new RangeError(`${name} must be finite`);
  }
};

const assertPositive = (value: number, name: string) => {
  assertFinite(value, name);
  if (value <= 0) {
    throw new RangeError(`${name} must be greater than zero`);
  }
};

const validateBounds = (bounds: ViewportScaleBounds) => {
  assertPositive(bounds.minScale, "minScale");
  assertPositive(bounds.maxScale, "maxScale");
  if (bounds.minScale > bounds.maxScale) {
    throw new RangeError("minScale must not exceed maxScale");
  }
};

const validatePoint = (point: ViewportPoint) => {
  assertFinite(point.x, "point.x");
  assertFinite(point.y, "point.y");
};

const clampScale = (scale: number, bounds: ViewportScaleBounds) => {
  assertFinite(scale, "scale");
  return Math.min(bounds.maxScale, Math.max(bounds.minScale, scale));
};

const normalizeState = (state: ViewportState, bounds: ViewportScaleBounds): ViewportState => {
  assertFinite(state.offsetX, "offsetX");
  assertFinite(state.offsetY, "offsetY");

  return {
    offsetX: state.offsetX,
    offsetY: state.offsetY,
    scale: clampScale(state.scale, bounds),
  };
};

const createDefaultState = (config: ViewportConfig, bounds: ViewportScaleBounds): ViewportState => {
  const state = config.defaultState ?? {};

  return normalizeState(
    {
      offsetX: state.offsetX ?? DEFAULT_VIEWPORT_STATE.offsetX,
      offsetY: state.offsetY ?? DEFAULT_VIEWPORT_STATE.offsetY,
      scale: state.scale ?? DEFAULT_VIEWPORT_STATE.scale,
    },
    bounds,
  );
};

export const createViewportModel = (config: ViewportConfig = {}): ViewportModel => {
  const bounds = Object.freeze({ ...(config.bounds ?? DEFAULT_VIEWPORT_BOUNDS) });
  validateBounds(bounds);
  const defaultState = createDefaultState(config, bounds);

  const createState = (state: Partial<ViewportState> = {}): ViewportState =>
    normalizeState(
      {
        offsetX: state.offsetX ?? defaultState.offsetX,
        offsetY: state.offsetY ?? defaultState.offsetY,
        scale: state.scale ?? defaultState.scale,
      },
      bounds,
    );

  const worldToStage = (state: ViewportState, point: ViewportPoint): ViewportPoint => {
    const currentState = normalizeState(state, bounds);
    validatePoint(point);

    return {
      x: point.x * currentState.scale + currentState.offsetX,
      y: point.y * currentState.scale + currentState.offsetY,
    };
  };

  const stageToWorld = (state: ViewportState, point: ViewportPoint): ViewportPoint => {
    const currentState = normalizeState(state, bounds);
    validatePoint(point);

    return {
      x: (point.x - currentState.offsetX) / currentState.scale,
      y: (point.y - currentState.offsetY) / currentState.scale,
    };
  };

  const panTo = (state: ViewportState, offset: ViewportPoint): ViewportState => {
    const currentState = normalizeState(state, bounds);
    validatePoint(offset);

    return normalizeState(
      {
        offsetX: offset.x,
        offsetY: offset.y,
        scale: currentState.scale,
      },
      bounds,
    );
  };

  const panBy = (state: ViewportState, delta: ViewportPoint): ViewportState => {
    const currentState = normalizeState(state, bounds);
    validatePoint(delta);

    return normalizeState(
      {
        offsetX: currentState.offsetX + delta.x,
        offsetY: currentState.offsetY + delta.y,
        scale: currentState.scale,
      },
      bounds,
    );
  };

  const setScale = (state: ViewportState, scale: number): ViewportState => {
    const currentState = normalizeState(state, bounds);

    return normalizeState(
      {
        offsetX: currentState.offsetX,
        offsetY: currentState.offsetY,
        scale: clampScale(scale, bounds),
      },
      bounds,
    );
  };

  const zoomAtWorld = (
    state: ViewportState,
    scale: number,
    worldAnchor: ViewportPoint,
    stageAnchor?: ViewportPoint,
  ): ViewportState => {
    const currentState = normalizeState(state, bounds);
    validatePoint(worldAnchor);
    const nextStageAnchor = stageAnchor ?? worldToStage(currentState, worldAnchor);
    validatePoint(nextStageAnchor);
    const nextScale = clampScale(scale, bounds);

    return normalizeState(
      {
        offsetX: nextStageAnchor.x - worldAnchor.x * nextScale,
        offsetY: nextStageAnchor.y - worldAnchor.y * nextScale,
        scale: nextScale,
      },
      bounds,
    );
  };

  const zoomTo = (
    state: ViewportState,
    scale: number,
    stageAnchor: ViewportPoint = ORIGIN,
  ): ViewportState => {
    const currentState = normalizeState(state, bounds);
    const worldAnchor = stageToWorld(currentState, stageAnchor);
    return zoomAtWorld(currentState, scale, worldAnchor, stageAnchor);
  };

  const zoomBy = (
    state: ViewportState,
    factor: number,
    stageAnchor: ViewportPoint = ORIGIN,
  ): ViewportState => {
    assertPositive(factor, "factor");
    const currentState = normalizeState(state, bounds);
    return zoomTo(currentState, currentState.scale * factor, stageAnchor);
  };

  return {
    bounds: Object.freeze({ ...bounds }),
    clampScale: (scale) => clampScale(scale, bounds),
    createState,
    defaultState: Object.freeze({ ...defaultState }),
    panBy,
    panTo,
    reset: () => ({ ...defaultState }),
    setScale,
    stageToWorld,
    worldToStage,
    zoomAtWorld,
    zoomBy,
    zoomTo,
  };
};
